import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MsalModule,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import { CommonModule, registerLocaleData } from '@angular/common';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ReactiveFormsModule } from '@angular/forms';
import { FeatherModule } from 'angular-feather';
import { allIcons } from 'angular-feather/icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideToastr } from 'ngx-toastr';
import localeNl from '@angular/common/locales/nl';
import localeEn from '@angular/common/locales/en';

import { AppComponent } from './app/app.component';
import { PipeModule } from '@v2/shared/pipes/pipe.module';
import { SecurityModule } from '@v2/core/security/security.module';
import { AppRoutingModule } from './app/app-routing.module';
import { LayoutModule } from './app/layout/layout.module';
import { OfflineInterceptor } from './app/maintenance/offline-interceptor';
import { msalConfig } from './app/auth/auth-config';
import { AlertToastComponent } from '@v2/shared/components/alert/alert.toast';
import { LoadingInterceptor } from './app/layout/loader/loading.interceptor';
import { BoabaasAuthInterceptor } from './app/auth/boabaas.auth.interceptor';
import { LocaleWrapper } from '@v2/core/language/localewrapper';
import { authHeadersInterceptorFn } from './app/auth/auth-headers.interceptor';

function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: msalConfig.clientId,
      authority: msalConfig.policies.signUpSignIn.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
      knownAuthorities: [msalConfig.authorityDomain],
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });
}
function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[] | null>();

  // The e-mail unsubscribe endpoints are anonymous (authenticated by the LoginToken in the
  // request body); a null entry before the wildcard stops MSAL from forcing a B2C login.
  const anonymousEndpoints = ['/v1/profile/subscriptionsettings', '/v1/profile/notificationsubscriptionpreference'];

  for (const res of msalConfig.protectedResources) {
    for (const endpoint of anonymousEndpoints) {
      protectedResourceMap.set(`${res.uri}${endpoint}`, null);
    }
    // v5 protectedResourceMap uses strict URL matching; append a wildcard so sub-paths
    // of each configured base still receive the bearer token.
    protectedResourceMap.set(`${res.uri}/*`, res.scopes);
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

function loggerCallback(logLevel: LogLevel, message: string): void {
  // eslint-disable-next-line no-console
  if (logLevel == LogLevel.Error) console.error(message);
  // eslint-disable-next-line no-console
  else console.log(message);
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: msalConfig.scopes,
      // get state() {
      //   return location.href;
      // },
    },
    // loginFailedRoute: '/login-failed',
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      CommonModule,
      BrowserModule,
      LayoutModule,
      AppRoutingModule,
      MsalModule,
      SecurityModule,
      ReactiveFormsModule,
      PipeModule.forRoot(),
      FeatherModule.pick(allIcons),
      NgScrollbarModule,
      MatProgressSpinnerModule,
    ),
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
    }),
    { provide: HTTP_INTERCEPTORS, useClass: BoabaasAuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OfflineInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideHttpClient(withInterceptors([authHeadersInterceptorFn]), withInterceptorsFromDi()),
    provideToastr({
      toastComponent: AlertToastComponent,
      preventDuplicates: true,
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useClass: LocaleWrapper },
  ],
  // eslint-disable-next-line no-console
}).catch((err) => console.error(err));

registerLocaleData(localeNl);
registerLocaleData(localeEn);
